import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { bool, object } from 'prop-types';
import { Footer, IconSpinner, LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import { useConfiguration } from '../../context/configurationContext';
import { loadStripe } from '@stripe/stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { withViewport } from '../../util/uiHelpers';
import css from './ProgramOnboardingPage.module.css';
// import onboardingBanner from '../../assets/onboardingBanner.png';
import illustration from '../../assets/program_img1.png';
import FAQSection from './Sections/FAQSection';
import ProgramSection from './Sections/ProgramSection';
import { fetchStripeCheckoutSession } from './ProgramOnboardingPage.duck';
import { useHistory, useLocation } from 'react-router-dom';
import { parse } from '../../util/urlHelpers';
import SubscriptionModal from './Modal/SubscriptionModal';
import Cookies from 'js-cookie';
import { decrypt, handleDeferredLanguageSwitch } from '../../util/genericHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { freeTrialOption } from '../../util/enums';
import { toast } from 'react-toastify';
import { getLifecycleBrowserTimezone } from '../../util/lifecycleTimezone';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;

const ProgramOnboardingPage = ({ viewport, forceCorporateCoupon = false }) => {
  const intl = useIntl();
  const config = useConfiguration();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();

  const search = parse(location.search);
  const sessionId = search?.session_id;

  const state = useSelector(state => state);
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const { productPrices = [], fetchProductPriceInProgress, stripeSessionError } =
    state?.ProgramOnboardingPage || {};

  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [isSession, setIsSession] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [confirmedSubscription, setConfirmedSubscription] = useState(null);

  const schemaTitle = intl.formatMessage({ id: 'ProgramOnboardingPage.schemaTitle' });
  const schemaDescription = intl.formatMessage({ id: 'ProgramOnboardingPage.schemaDescription' });
  const schemaImage = `${config.marketplaceRootURL}${illustration}`;

  // 👇 Show toast passed via history.state (e.g., from account deletion), then clear it
  useEffect(() => {
    const arrivalToast = location?.state?.toast;
    if (!arrivalToast) return;
    const { type = 'info', message, options } = arrivalToast || {};
    if (message) {
      const fn = toast[type] || toast;
      fn(message, options);
    }
    // Clear toast from state so it won't re-fire on refresh/back
    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: { ...(location.state || {}), toast: undefined },
    });
  }, [location, history]);

  const fetchPaymentIntent = async () => {
    setIsSession(true);

    const password = Cookies.get('p_key');
    const email = Cookies.get('e_key');
    const firstName = Cookies.get('f_key');
    const lastName = Cookies.get('l_key');
    const dob = Cookies.get('d_key');
    const city = Cookies.get('ci_key');
    const country = Cookies.get('co_key');
    const preferredLang = Cookies.get('pr_key');
    const voucher = Cookies.get('vou_key');
    const couponCode = Cookies.get('cc_key') || voucher;
    const couponId = Cookies.get('cid_key');
    const subscriptionType = Cookies.get('st_key');

    const decryptPassword = decrypt(password, encryptionKey);
    const decryptEmail = decrypt(email, encryptionKey);
    const isCorporateCouponUser =
      subscriptionType === 'corporate' || Boolean(couponCode || voucher);
    const browserTimezone = getLifecycleBrowserTimezone();

    const signupParams = {
      email: decryptEmail,
      password: decryptPassword,
      firstName: firstName,
      lastName: lastName,
      protectedData: {
        dob: dob,
        city: city,
        country: country,
        preferredLang: preferredLang,
        voucher: voucher ? voucher : null,
        couponCode: couponCode || null,
        couponId: couponId || null,
        subscriptionType: subscriptionType || (isCorporateCouponUser ? 'corporate' : 'regular'),
      },
      privateData: {
        isCorporateUser: isCorporateCouponUser,
        ...(browserTimezone ? { timezone: browserTimezone, timezoneSource: 'browser_intl' } : {}),
      },
    };

    try {
      const response = await dispatch(fetchStripeCheckoutSession({ sessionId, signupParams }));
      setConfirmedSubscription(response?.subscription || null);
    } catch (error) {
      console.log(error, 'error in fetching session');
      // You may want to surface this, too
      toast.error(error?.message || 'Failed to initialize checkout session.', {
        position: 'top-right',
        autoClose: 8000,
      });
      throw new Error(error);
    } finally {
      setOpenSubscriptionModal(true);
      setIsSession(false);
    }
  };

  useEffect(() => {
    if (sessionId && Cookies.get('e_key') && Cookies.get('p_key')) {
      fetchPaymentIntent();
    }
  }, [scrollingDisabled, sessionId]);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('pendingLanguageSwitch');
      }
    };
  }, []);

  const handleNext = async () => {
    setIsNextLoading(true);

    handleDeferredLanguageSwitch();

    const path = createResourceLocatorString('MenopauseResourcePage', routeConfiguration, {}, {});
    if (typeof window !== 'undefined') {
      window.location.href = path;
    }
  };

  const handleCancel = () => {
    history.push(createResourceLocatorString('MenopauseResourcePage', routeConfiguration, {}, {}));
  };

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={schemaTitle}
      description={schemaDescription}
      noIndex
      facebookImages={[{ url: illustration, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${illustration}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer isFullWidthHeader={true} />}
        footer={<Footer hideNewsLetter />}
      >
        <div className={css.programsContainer} data-testid="register-page">
          <div className={css.contentWidth}>
            <div className={css.exploreSec}>
              <h2 data-testid="register-page-title">
                <FormattedMessage id="ProgramOnboardingPage.heading" />
              </h2>
              <p>
                <FormattedMessage id="ProgramOnboardingPage.subheading" />
              </p>
            </div>
          </div>
        </div>

        {fetchProductPriceInProgress || isSession ? (
          <div className={css.programOnboardingPageLoader}>
            <IconSpinner />
          </div>
        ) : (
          <div className={css.programOnboardingPageMain}>
            <ProgramSection
              intl={intl}
              productPrices={[...(productPrices || [])]}
              fetchProductPriceInProgress={fetchProductPriceInProgress}
              stripePromise={stripePromise}
              dispatch={dispatch}
              handleCancel={handleCancel}
              viewport={viewport}
              setOpenSubscriptionModal={setOpenSubscriptionModal}
              forceCorporateCoupon={forceCorporateCoupon}
            />
            <FAQSection intl={intl} />
            <SubscriptionModal
              isOpen={openSubscriptionModal}
              onNext={handleNext}
              intl={intl}
              stripeSessionError={stripeSessionError}
              isNextLoading={isNextLoading}
              subscription={confirmedSubscription}
            />
          </div>
        )}
      </LayoutSingleColumn>
    </Page>
  );
};

ProgramOnboardingPage.propTypes = {
  viewport: object.isRequired,
  forceCorporateCoupon: bool,
};

export default withViewport(ProgramOnboardingPage);
